﻿import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/typedDispatch";
import {
  selectMappings,
  updateFields,
  saveMappingInfo,
  selectSelectedSchema,
  selectArtifactHeaderConsolidatedInfo,
  selectArtifactDetailConsolidatedInfo,
  testScript,
} from "@/features/mapSchema/mapSchemaSlice";
import {
  Typography,
  Container,
  FormControl,
  Select,
  MenuItem,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddEditInboundMappingGrid from "./AddEditInboundMappingGrid";
import OptionalFieldsGrid from "./OptionalFieldsGrid";
import ScriptEditor from "@/components/GenericComponents/ScriptEditor";
import "@/components/GenericComponents/ScriptEditor.css";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";
import type { IArtifactInboundMapping, IMapSchemaField } from "@/features/sharedTypes";

interface IAddEditInboundMapping {
  onBack: () => void;
  isEditMode: boolean;
  recordToEdit: IArtifactInboundMapping;
}

const AddEditInboundMapping = ({
  onBack,
  isEditMode,
  recordToEdit,
}: IAddEditInboundMapping) => {
  const dispatch = useAppDispatch();

  const [isSaveBtnDisabled] = useState(false);

  const currentMappingList = useSelector(selectMappings);

  const selectedSchema = useSelector(selectSelectedSchema);

  const { script } = currentMappingList;

  const [scriptMapValue, setScriptMapValue] = useState("");

  const handleSave = () => {
    const connectionId = selectedSchema.ConnectionId;
    const artifactType = selectedSchema.ArtifactType;
    const dataSourceType = selectedSchema.SchemaType;
    const connectionArtifactSchemaId =
      selectedSchema.ConnectionArtifactSchemaId;
    const mapType =
      selectedSchema.Direction === "Outbound" ? "outbound" : "inbound";
    dispatch(
      saveMappingInfo(
        mapType,
        connectionId,
        artifactType,
        dataSourceType,
        connectionArtifactSchemaId,
        onBack,
        isEditMode,
      ),
    );
  };

  const artifactHeaderConsolidatedInfo: IMapSchemaField[] = useSelector(
    selectArtifactHeaderConsolidatedInfo,
  );
  const artifactDetailConsolidatedInfo: IMapSchemaField[] = useSelector(
    selectArtifactDetailConsolidatedInfo,
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedOptionalRows, setSelectedOptionalRows] = useState<string[]>(
    [],
  );

  const [rowSelectionModel, setRowSelectionModel] = useState<string[]>([]);
  const [optionalRowSelectionModel, setOptionalRowSelectionModel] = useState<
    string[]
  >([]);

  const handleTestScript = async () => {
    await testScript(dispatch, script, scriptMapValue);
  };

  useEffect(() => {
    if (rowSelectionModel.length > 0) {
      setOptionalRowSelectionModel([]);
    }
  }, [rowSelectionModel, setOptionalRowSelectionModel]);
  useEffect(() => {
    if (optionalRowSelectionModel.length > 0) {
      setRowSelectionModel([]);
    }
  }, [optionalRowSelectionModel, setRowSelectionModel]);

  //header and detail
  useEffect(() => {
    if (currentMappingList.mappingArea === 0) {
      if (isEditMode && artifactHeaderConsolidatedInfo.length > 0) {
        const targetFieldName = recordToEdit?.FieldName;
        if (targetFieldName !== null) {
          const matchingRow = artifactHeaderConsolidatedInfo.find(
            (row) => row.field === targetFieldName,
          );
          if (matchingRow) {
              if (matchingRow.isOptional)
                setSelectedOptionalRows([matchingRow.id])
              else
                setSelectedRows([matchingRow.id]);
          }
        }
      }
    } else if (currentMappingList.mappingArea === 1) {
      if (isEditMode && artifactDetailConsolidatedInfo.length > 0) {
        const targetFieldName = recordToEdit?.FieldName;
        if (targetFieldName !== null) {
          const matchingRow = artifactDetailConsolidatedInfo.find(
            (row) => row.field === targetFieldName,
          );
          if (matchingRow) {
              if (matchingRow.isOptional)
                  setSelectedOptionalRows([matchingRow.id]);
              else
                setSelectedRows([matchingRow.id]);
          }
        }
      }
    }
  }, [isEditMode, artifactDetailConsolidatedInfo, artifactHeaderConsolidatedInfo, currentMappingList.mappingArea, recordToEdit?.FieldName]);

  return (
    <>
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton variant="secondary" onClick={onBack} sx={{ mr: 1 }}>
        Back
      </FooterButton>
      <FooterButton
        variant="primary"
        onClick={handleSave}
        disabled={isSaveBtnDisabled}
      >
        Save
      </FooterButton>

      <Container className="content-container">
        <Box
          sx={{
            padding: "10px 40px",
            background: "white",
            borderRadius: "var(--main-border-radius)",
          }}
        >
          {/* Two-column layout */}
          <Box display="flex" flexDirection="row" gap={4} marginTop={2}>
            {/* Left column */}
            <Box display="flex" flexDirection="column" flex={1}>
              <Box marginBottom={5}>
                <Typography variant="h2">EDI Inbound Map</Typography>
              </Box>

              {!isEditMode && (
                <Box display="flex" alignItems="center" marginBottom={2}>
                  <div style={{ width: "250px" }}>Mapping Area:</div>
                  <FormControl fullWidth>
                    <Select
                      labelId="connection-type-label"
                      id="connection-type"
                      variant="outlined"
                      value={currentMappingList.mappingArea ?? ""}
                      label="Connection Type"
                      onChange={(e) =>
                        dispatch(
                          updateFields({
                            key: "mappingArea",
                            value: e.target.value,
                          }),
                        )
                      }
                      margin="none"
                      sx={{ width: 400 }}
                      disabled={false}
                      MenuProps={{
                        style: { zIndex: 1500 },
                      }}
                    >
                      <MenuItem value={0}>Header</MenuItem>
                      <MenuItem value={1}>Detail</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Mapping Segment:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({
                        key: "mappingSegment",
                        value: e.target.value,
                      }),
                    )
                  }
                  value={currentMappingList.mappingSegment ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Value Position:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({
                        key: "valuePosition",
                        value: e.target.value,
                      }),
                    )
                  }
                  value={currentMappingList.valuePosition ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Condition Segment:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({
                        key: "conditionSegment",
                        value: e.target.value,
                      }),
                    )
                  }
                  value={currentMappingList.conditionSegment ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Occurrence:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({
                        key: "occurrence",
                        value: e.target.value,
                      }),
                    )
                  }
                  value={currentMappingList.occurrence ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>
            </Box>

            {/* Right column */}
            <Box display="flex" flexDirection="column" flex={1}>
              <Box marginBottom={2}>
                <ScriptEditor
                  value={script}
                  onChange={(e: string) => {
                    dispatch(
                      updateFields({
                        key: "script",
                        value: e,
                      }),
                    );
                  }}
                  variableCompletions={["value"]}
                  highlightValue
                />
              </Box>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>
                  Test Value (
                  <span className="script-variable-highlight">value</span>):
                </div>
                <TextField
                  variant="outlined"
                  value={scriptMapValue}
                  onChange={(e) => {
                    setScriptMapValue(e.target.value);
                  }}
                  sx={{ width: 400 }}
                />
                <Button
                  className="btn-secondary"
                  onClick={handleTestScript}
                  style={{ marginLeft: 50 }}
                >
                  Test
                </Button>
              </Box>

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Prefix:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({ key: "prefix", value: e.target.value }),
                    )
                  }
                  value={currentMappingList.prefix ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>

              <Box display="flex" alignItems="center" marginBottom={2}>
                <div style={{ width: "200px" }}>Suffix:</div>
                <TextField
                  variant="outlined"
                  onChange={(e) =>
                    dispatch(
                      updateFields({ key: "suffix", value: e.target.value }),
                    )
                  }
                  value={currentMappingList.suffix ?? ""}
                  sx={{ width: 400 }}
                />
              </Box>

              <Box display="flex" alignItems="center">
                <FormControlLabel
                  style={{ marginLeft: 0 }}
                  control={
                    <Checkbox
                      onChange={(e) =>
                        dispatch(
                          updateFields({
                            key: "showInReport",
                            value: e.target.checked,
                          }),
                        )
                      }
                      checked={currentMappingList.showInReport ?? false}
                    />
                  }
                  label="Show in Report"
                />
              </Box>
            </Box>
          </Box>

          <Box>
            <AddEditInboundMappingGrid
              mappingArea={currentMappingList.mappingArea}
              selectedRows={selectedRows}
              isEditMode={isEditMode}
              rowSelectionModel={rowSelectionModel}
              setRowSelectionModel={setRowSelectionModel}
            ></AddEditInboundMappingGrid>
          </Box>

          <Box>
            <OptionalFieldsGrid
              mappingArea={currentMappingList.mappingArea}
              selectedOptionalRows={selectedOptionalRows}
              artifactType={selectedSchema.ArtifactType}
              rowSelectionModel={optionalRowSelectionModel}
              setRowSelectionModel={setOptionalRowSelectionModel}
            ></OptionalFieldsGrid>
          </Box>
        </Box>
      </Container>
    </>
  );
};
export default AddEditInboundMapping;
